import React, { useEffect, useState } from "react";
import { ButtonGroup, Image } from "react-bootstrap";
import { useAccount, useNetwork } from "wagmi";

import { MyButton } from "components/button";
import InputField from "./components/InputField";
import {
  addVanryTokenToMetamask,
  copyToClipboard,
  envChains,
  getDecimalLength,
  getTruncatedAddress,
  handleDeepLink,
  status,
  walletIcon,
} from "utils/helper";
import { useRootContext } from "context/app-context";
import { approveAllowance, swapToken } from "services/swap-tvk";
import ConvertSwapToken from "./components/ConvertSwapToken";
import { useSwapContext } from "context/swap-context";

import "./CustomForm.scss";
import polygonIcon from "assets/images/polygonIcon.svg";
import RejectedTransactionModal from "components/modals/ErrorModalTransactions";

const SwapForm = () => {
  const [isLoading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [copied, setCopied] = useState(false);

  const { chain } = useNetwork();
  const { isConnected, address } = useAccount();
  const { handleToggleWalletSidebar, isSignWallet, selectedNetwork } =
    useRootContext();
  const {
    tvkAbi,
    swapAbi,
    inputValue,
    trxHashType,
    vanryContract,
    tvkSwapContract,
    tvkTokenContract,
    userTokenBalance,
    isSwappingActive,
    approvalAllowance,
    setTrxData,
    setInputValue,
    setTrxHashType,
  } = useSwapContext();

  const handleApprovalBtn = () => {
    const approvalObj = {
      tokenContract: tvkTokenContract,
      swapContract: tvkSwapContract,
      tokenContractAbi: tvkAbi,
      amount: inputValue,
    };
    setLoading(true);
    approveAllowance(approvalObj)
      .then((hash) => {
        const obj = {
          type: status.APPROVED_ALLOWANCE,
          t_hash: hash,
        };
        const trxDataObj = {
          txId: hash,
          to: tvkTokenContract,
          from: address,
          tvk: inputValue,
          txType: "Approval",
          chainId: selectedNetwork,
        };
        setTrxData(trxDataObj);
        setTrxHashType(obj);
        localStorage.setItem("txHashType", JSON.stringify(obj));
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        if (
          err
            ?.toString()
            ?.includes("TransactionExecutionError: User rejected the request")
        ) {
          setErrorMsg("You did not provide an approval.");
        }
        if (
          err
            ?.toString()
            ?.includes(
              "TransactionExecutionError: An internal error was received"
            )
        ) {
          setErrorMsg(
            "Your transaction has failed, please try again after some time."
          );
        }
        setLoading(false);
      });
  };

  const handleSwapBtn = () => {
    const approvalObj = {
      swapContract: tvkSwapContract,
      tokenContractAbi: swapAbi,
      amount: inputValue,
    };
    setLoading(true);
    swapToken(approvalObj)
      .then((hash) => {
        const obj = {
          type: status.SWAPPED,
          t_hash: hash,
        };
        const trxDataObj = {
          txId: hash,
          to: tvkSwapContract,
          from: address,
          tvk: inputValue,
          txType: "SwapToken",
          chainId: selectedNetwork,
        };
        setTrxData(trxDataObj);
        setTrxHashType(obj);
        localStorage.setItem("txHashType", JSON.stringify(obj));
        setLoading(false);
      })
      .catch((err) => {
        if (err?.toString()?.includes("Swap Contract: Swap is not enabled!")) {
          setErrorMsg("Swapping is not enabled!");
        }
        if (
          err
            ?.toString()
            ?.includes("TransactionExecutionError: User rejected the request")
        ) {
          setErrorMsg("You have denied the token swapping");
        }
        if (
          err
            ?.toString()
            ?.includes(
              "TransactionExecutionError: An internal error was received"
            )
        ) {
          setErrorMsg(
            "Your transaction has failed, please try again after some time."
          );
        }

        if (
          err?.toString()?.includes("ERC20: transfer amount exceeds balance")
        ) {
          setErrorMsg("Transfer amount exceeds balance.");
        }
        setLoading(false);
      });
  };

  const handleChange = (e) => {
    const value = e.target.value;
    const isDecimalLength =
      getDecimalLength(value.toString()) > 18 ? false : true;
    if (isDecimalLength) {
      if (value) {
        if (value <= Number(userTokenBalance)) {
          setInputValue(value);
        }
      } else {
        setInputValue("");
      }
    }
  };

  const handleButtonManage = () => {
    if (!envChains.includes(selectedNetwork) && isSignWallet) {
      return (
        <MyButton
          text={"Please switch to either Ethereum or Polygon."}
          className="custom-btn md dark heroButton rounded"
          disabled={true}
        />
      );
    } else if (!isSignWallet) {
      return (
        <MyButton
          text={isConnected && !isSignWallet ? "connecting" : "connect wallet"}
          className="custom-btn md dark heroButton rounded"
          handleClick={() => handleToggleWalletSidebar()}
        />
      );
    } else if (!isSwappingActive) {
      return (
        <MyButton
          text={"Token swapping has not started"}
          className="custom-btn md dark heroButton rounded"
          disabled={true}
        />
      );
    } else {
      if (isLoading || trxHashType.t_hash) {
        return (
          <MyButton
            className="custom-btn md dark heroButton rounded"
            text={trxHashType.t_hash ? "Pending" : ""}
            isLoading={isLoading}
            disabled={trxHashType.t_hash}
          />
        );
      } else if (inputValue <= Number(approvalAllowance) && inputValue > 0) {
        return (
          <MyButton
            text="Swap"
            className="custom-btn md dark heroButton rounded"
            handleClick={handleSwapBtn}
            disabled={inputValue <= 0}
          />
        );
      } else {
        return (
          <MyButton
            text="approve"
            className="custom-btn md dark heroButton rounded"
            handleClick={handleApprovalBtn}
            disabled={inputValue <= 0}
          />
        );
      }
    }
  };

  const handleAddTokenInMetamask = async () => {
    let provider = null;
    if (window.ethereum?.providers) {
      provider = window.ethereum?.providers?.find((p) => p?.isMetaMask);
    } else if (window.ethereum.isMetaMask) {
      provider = window.ethereum;
    }
    if (provider) {
      const res = await addVanryTokenToMetamask(vanryContract, provider);
      if (res.status === "fail") {
        if (res.payload?.message === "User rejected the request.") {
          setErrorMsg("User Denied Authorization");
        }
      }
    } else {
      handleDeepLink();
    }
  };

  const handleCopyAddress = (address) => {
    copyToClipboard(address).then((res) => {
      if (res === "success") {
        setCopied(true);
      }
    });
  };

  useEffect(() => {
    let timeout = null;
    timeout = setTimeout(() => {
      setCopied(false);
    }, 1500);
    return () => clearTimeout(timeout);
  }, [copied]);

  return (
    <>
      <div className="hero-form">
        <div className="form-wrapper">
          {/* <h3 className="title">Swap</h3> */}

          <div className="inputFields">
            <InputField
              className="fromInput"
              inputTitle="Swap From"
              inputValue={inputValue}
              accountName={
                chain?.id === envChains[0]
                  ? "Polygon Mainnet"
                  : "Ethereum Mainnet"
              }
              accountImg={
                chain?.id === envChains[0]
                  ? polygonIcon
                  : process.env.REACT_APP_S3_IMAGE_PATH + "ethIcon.svg"
              }
              coinName="TVK"
              coinImage={process.env.REACT_APP_S3_IMAGE_PATH + "tvk.svg"}
              userTokenBalance={userTokenBalance}
              approvalAllowance={approvalAllowance}
              handleChange={handleChange}
              setInputValue={setInputValue}
            />

            <div className="d-flex align-items-center justify-content-center converter">
              <div className="convert">
                <Image
                  src={process.env.REACT_APP_S3_IMAGE_PATH + "convertArrow.svg"}
                  fluid
                />
              </div>
            </div>

            <ConvertSwapToken
              className="toInput"
              title="Swap To"
              accountName={
                chain?.id === envChains[0]
                  ? "Polygon Mainnet"
                  : "Ethereum Mainnet"
              }
              accountImg={
                chain?.id === envChains[0]
                  ? polygonIcon
                  : process.env.REACT_APP_S3_IMAGE_PATH + "ethIcon.svg"
              }
              coinName="VANRY"
              coinImage={process.env.REACT_APP_S3_IMAGE_PATH + "vTokenIcon.svg"}
              inputValue={inputValue}
            />
          </div>
          {isSignWallet && (
            <p className="info-bottom">
              Metamask users please ensure the exact amount you input above is
              represented in spending approval in Metamask to avoid rounding
              errors; <a href={"/#confirm-stake"}>See FAQs</a>
            </p>
          )}
          {handleButtonManage()}
        </div>

        {isSignWallet && (
          <ButtonGroup className="btn-group-bottom">
            <MyButton
              text={getTruncatedAddress(vanryContract)}
              className="custom-btn md dim-border icon-btn rounded"
              iconButtonImage={
                envChains[0] === selectedNetwork
                  ? polygonIcon
                  : process.env.REACT_APP_S3_IMAGE_PATH + "ethIcon.svg"
              }
              arrowIcon={
                copied
                  ? process.env.REACT_APP_S3_IMAGE_PATH + "tick.svg"
                  : process.env.REACT_APP_S3_IMAGE_PATH + "copyIcon.svg"
              }
              handleClick={() => handleCopyAddress(vanryContract)}
            />
            <MyButton
              text="Add $VANRY to Metamask"
              className="custom-btn md dim-border icon-btn rounded"
              arrowIcon={walletIcon.metaMask}
              handleClick={handleAddTokenInMetamask}
            />
          </ButtonGroup>
        )}
      </div>

      {errorMsg && (
        <RejectedTransactionModal
          show={errorMsg ?? false}
          onHide={() => setErrorMsg("")}
          errorMessage={errorMsg}
        />
      )}
    </>
  );
};

export default SwapForm;
